import { motion } from 'framer-motion'
import { forwardRef } from 'react'

/**
 * PrimeLab Button — brief §1.1.
 * Variantes: primary (terracota), secondary (outline), ghost (sin borde).
 * Coexiste con Button.jsx legacy hasta migración por callsite (ver CLAUDE.md).
 */

const SIZES = {
  sm: { height: 40, paddingX: 16, fontSize: 14 },
  md: { height: 48, paddingX: 20, fontSize: 15 },
  lg: { height: 56, paddingX: 24, fontSize: 16 },
}

const VARIANTS = {
  primary: {
    background: 'var(--accent)',
    color: 'var(--pl-cream)',
    border: 'none',
  },
  secondary: {
    background: 'transparent',
    color: 'var(--accent)',
    border: '1px solid var(--accent)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-soft)',
    border: 'none',
  },
}

function Spinner({ color = 'currentColor' }) {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="7" stroke={color} strokeOpacity="0.25" strokeWidth="2" />
      <path d="M9 2a7 7 0 0 1 7 7" stroke={color} strokeWidth="2" strokeLinecap="round">
        <animateTransform attributeName="transform" type="rotate" from="0 9 9" to="360 9 9" dur="0.8s" repeatCount="indefinite" />
      </path>
    </svg>
  )
}

const PLButton = forwardRef(function PLButton(
  { variant = 'primary', size = 'md', loading, disabled, fullWidth = false, leftIcon, rightIcon, children, onClick, style, type = 'button', ...rest },
  ref,
) {
  const sz = SIZES[size] || SIZES.md
  const vr = VARIANTS[variant] || VARIANTS.primary
  const isDisabled = disabled || loading

  return (
    <motion.button
      ref={ref}
      type={type}
      whileTap={isDisabled ? undefined : { scale: 0.98 }}
      onClick={isDisabled ? undefined : onClick}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      style={{
        ...vr,
        width: fullWidth ? '100%' : undefined,
        height: sz.height,
        padding: `0 ${sz.paddingX}px`,
        fontSize: sz.fontSize,
        fontWeight: 'var(--weight-medium)',
        fontFamily: 'var(--font-sans)',
        borderRadius: 'var(--radius-md)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled && !loading ? 0.4 : 1,
        transition: 'opacity var(--duration-fast) var(--ease-out-pl), background var(--duration-fast) var(--ease-out-pl)',
        letterSpacing: '0.01em',
        userSelect: 'none',
        ...style,
      }}
      {...rest}
    >
      {loading ? <Spinner /> : (
        <>
          {leftIcon}
          <span>{children}</span>
          {rightIcon}
        </>
      )}
    </motion.button>
  )
})

export default PLButton
